import _ from "lodash";
import { Table, SelectColumnFilter } from "../Table/Table";
import { useEffect, useMemo, useState } from "react";
import { Button, Icon, Popup } from "semantic-ui-react";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";

export const ResolutionsTable = ({
  items,
  handleUpdate,
  updatingRes,
  handleDelete,
  handleUpdateActivation,
  t,
}) => {
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [confirmationModalData, setConfirmationModalData] = useState({
    text: "",
    headerText: "",
    icon: "",
  });
  const [confirmationModalOnSuccess, setConfimationModalOnSuccess] = useState(() => {
    return;
  });
  const [confirmationModalDataForOnSuccess, setConfimationModalDataForOnSuccess] = useState({});

  const nativeResUsedInActiveCampaign = (nativeRes) => {
    if (!nativeRes || !nativeRes.campaigns) return true;
    const dateNow = new Date();
    return !!_.find(nativeRes.campaigns.items, (item) => {
      if (!item) return false;
      const campaign = item.campaign;
      // Native resolution has active campaigns if any campaign's end time is after now
      return campaign.end_time && new Date(campaign.end_time) >= dateNow;
    });
  };

  /* Filter and Cell functions use the function-syntax here to go around an eslint warning:
    "Component definition is missing display name. eslint(react/display-name)"
  */
  const columns = useMemo(
    () => [
      {
        Header: t("required_resolution"),
        accessor: "requiredResolution.value",
        filter: "text",
        width: 180,
      },
      {
        Header: t("native_resolution"),
        accessor: "value",
        filter: "text",
        width: 180,
      },
      {
        Header: t("formats"),
        accessor: (originalRow) => {
          const value = originalRow["type"];
          return value === "IMAGE" ? "jpg/png" : "mp4/mov/jpg/png";
        },

        Filter: function filter(props) {
          return <SelectColumnFilter {...props} t={t} />;
        },
        filter: "text",
        width: 90,
      },
      {
        Header: t("horizontal_cut"),
        accessor: (originalRow) => {
          const value = originalRow["horizontalCut"];
          return value;
        },
        Filter: function filter(props) {
          return <SelectColumnFilter {...props} t={t} />;
        },
        filter: "text",
        width: 80,
      },
      {
        Header: t("vertical_cut"),
        accessor: (originalRow) => {
          const value = originalRow["verticalCut"];
          return value;
        },
        Filter: function filter(props) {
          return <SelectColumnFilter {...props} t={t} />;
        },
        filter: "text",
        width: 80,
      },
      {
        Header: t("active"),
        accessor: (originalRow) => {
          const value = originalRow["active"];
          return value ? t("yes") : t("no");
        },
        Filter: function filter(props) {
          return <SelectColumnFilter {...props} t={t} />;
        },
        filter: "text",
        Cell: function UpdatableActiveCell({ value: initialValue, row }) {
          const [value, setValue] = useState(initialValue);

          const onChange = (e) => {
            setConfirmationModalData({
              text:
                e.target.value === t("no")
                  ? t("deactivate_digital_size_modal_text")
                  : t("activate_digital_size_modal_text"),
              headerText:
                e.target.value === t("no")
                  ? t("deactivate_digital_size_modal_header")
                  : t("activate_digital_size_modal_header"),
              icon: "edit outline",
            });
            setConfimationModalDataForOnSuccess(row.original);
            setConfimationModalOnSuccess(() => handleUpdateActivation);
            setConfirmationModalOpen(true);
          };

          useEffect(() => {
            setValue(initialValue);
          }, [initialValue]);

          return (
            /* The following eslint warning is disabled since using onBlur doesn't achieve the
               desired funcionality:
               "onBlur must be used instead of onchange, unless absolutely necessary 
               and it causes no negative consequences for keyboard only or screen reader users." 
            */
            /* eslint-disable-next-line jsx-a11y/no-onchange */
            <select value={value} onChange={onChange} disabled={updatingRes}>
              <option value={t("yes")}>{t("yes")}</option>
              <option value={t("no")}>{t("no")}</option>
            </select>
          );
        },
        width: 80,
      },
      {
        id: "updateDeleteRow",
        maxWidth: 10,
        Cell: function updateDeleteRow({ row }) {
          return (
            <>
              <Popup
                content={t("digital_size_update")}
                trigger={
                  <Button
                    basic
                    icon
                    size="mini"
                    onClick={() => handleUpdate(row.original)}
                    disabled={updatingRes || nativeResUsedInActiveCampaign(row.original)}
                  >
                    <Icon name="edit outline" />
                  </Button>
                }
              />
              <Popup
                content={t("digital_size_delete")}
                trigger={
                  <Button
                    basic
                    icon
                    size="mini"
                    onClick={() => {
                      setConfirmationModalData({
                        text: t("delete_digital_size_modal_text"),
                        headerText: t("digital_size_delete"),
                        icon: "delete",
                      });
                      setConfimationModalOnSuccess(() => handleDelete);
                      setConfimationModalDataForOnSuccess(row.original);
                      setConfirmationModalOpen(true);
                    }}
                    disabled={updatingRes || nativeResUsedInActiveCampaign(row.original)}
                  >
                    <Icon name="delete" />
                  </Button>
                }
              />
            </>
          );
        },
      },
    ],
    [handleUpdate, updatingRes, handleDelete, handleUpdateActivation, t]
  );

  const sortedItems = useMemo(() => {
    const compareResValues = (a1, a2, b1, b2) => {
      if (a1 < b1) return -1;
      else if (a1 > b1) return 1;
      // Horizontal resolution values identical -> compare vertical resolution values
      else {
        if (a2 < b2) return -1;
        else if (a2 > b2) return 1;
        else return 0;
      }
    };
    const compareRequiredResValues = (a, b) => {
      // Expected split input e.g. "123x456" -> requiredResHorA === "123" and requiredResVerA === "456"
      const requiredResHorA = parseInt(a.requiredResolution.value.split("x")[0]);
      const requiredResVerA = parseInt(a.requiredResolution.value.split("x")[1]);
      const requiredResHorB = parseInt(b.requiredResolution.value.split("x")[0]);
      const requiredResVerB = parseInt(b.requiredResolution.value.split("x")[1]);

      let result = compareResValues(
        requiredResHorA,
        requiredResVerA,
        requiredResHorB,
        requiredResVerB
      );

      // Required resolutions identical -> compare native resolutions
      if (result === 0) {
        // Split explanation same as above
        const nativeResHorA = parseInt(a.value.split("x")[0]);
        const nativeResVerA = parseInt(a.value.split("x")[1]);
        const nativeResHorB = parseInt(b.value.split("x")[0]);
        const nativeResVerB = parseInt(b.value.split("x")[1]);

        result = compareResValues(nativeResHorA, nativeResVerA, nativeResHorB, nativeResVerB);
      }
      return result;
    };

    return items ? [...items].sort(compareRequiredResValues) : [];
  }, [items]);

  return (
    <>
      <Table columns={columns} data={sortedItems} useTableGlobalFilter={true} t={t} />
      <ConfirmationModal
        data={confirmationModalData}
        open={confirmationModalOpen}
        setOpen={setConfirmationModalOpen}
        onSuccess={confirmationModalOnSuccess}
        dataForOnSuccess={confirmationModalDataForOnSuccess}
      />
    </>
  );
};
